import { useState } from "react";

import { useDebounce } from "~/hooks/use-debounce";
import type { Scale, ScaleBoundUnit, ScaleModel } from "~/types/hardware";
import { defaultScaleDecimals } from "~/utils/hardware/scale-precision";

import { ScalePrecisionStep } from "./scale-precision-step";
import { useScaleTrial } from "./use-scale-trial";

const SLIDER_SETTLE_MS = 250;

const MODEL_LABELS: Record<ScaleModel, string> = {
  pdn: "CAS PDN",
  pd2: "CAS PD-II"
};

type ScalePrecisionFlowProps = {
  scale: Scale;
  model: ScaleModel;
  unit: ScaleBoundUnit;
  /** What is stored today; null starts the slider where the wrapper's default is. */
  initialDecimals: number | null;
  onConfirm: (decimals: number) => void;
  onBack: () => void;
  onDone: () => void;
};

/**
 * Runs the precision step against the live scale. The slider moves at once;
 * the scale is asked again a moment later so a drag does not open the port
 * for every notch on the way.
 */
export const ScalePrecisionFlow = ({
  scale,
  model,
  unit,
  initialDecimals,
  onConfirm,
  onBack,
  onDone
}: ScalePrecisionFlowProps) => {
  const startDecimals = initialDecimals ?? defaultScaleDecimals(model, unit);
  const [decimals, setDecimals] = useState(startDecimals);
  const [trialDecimals, setTrialDecimals] = useState(startDecimals);
  const [confirmed, setConfirmed] = useState(false);

  const pushTrial = useDebounce(setTrialDecimals, SLIDER_SETTLE_MS);

  const trial = useScaleTrial({
    scale,
    model,
    unit,
    decimals: trialDecimals,
    waitFor: "stable"
  });

  // The reading on screen was produced with the value the scale was last asked
  // for, so that is the value that gets saved; a slider position the scale has
  // not answered yet cannot be confirmed.
  const verified = decimals === trialDecimals;

  return (
    <ScalePrecisionStep
      canConfirm={verified}
      decimals={decimals}
      modelLabel={MODEL_LABELS[model]}
      onBack={onBack}
      onConfirm={() => {
        onConfirm(trialDecimals);
        setConfirmed(true);
      }}
      onDecimalsChange={(next) => {
        setDecimals(next);
        pushTrial(next);
      }}
      onDone={onDone}
      onRetry={trial.retry}
      phase={confirmed ? "confirmed" : trial.phase}
      reading={trial.reading}
    />
  );
};
